import { DEFAULT_ARRANGEMENT, SECTION_ID_PATTERN } from '@yourstore/theme';
import type { BrandingDocument, BrandingSection, LocalizedText } from '../../../wire';

/**
 * **The page builder's document** (console redesign C2) — the branding
 * document itself, and the few pure operations the builder performs on it.
 *
 * There is no second model. The old form editor kept a *draft* shape of its
 * own (rows with an include box, pages as a sorted list) and translated it
 * back on publish; the builder edits the document the validator reads and the
 * canvas renders, because every translation between two shapes is a place for
 * a band to go missing. What the builder adds is exactly two things, both
 * written into the document so they survive a publish unchanged:
 *
 *  1. **An `id` on every section.** The canvas keys a band by its id, else by
 *     `type` / `type-N` (`apps/site`'s `arrangeSections`), so a band without
 *     one changes key when a neighbour of its type moves — and the selection
 *     jumps. The id given is the key the site already used (`services`,
 *     `gallery-2`), so every anchor and every `section:<id>` link in the nav
 *     keeps pointing where it pointed.
 *  2. **An explicit arrangement.** A tenant who has published nothing has no
 *     `sections`, and the site draws its default. The builder writes that
 *     default out, because indexes on the wire are positions in the list *as
 *     sent* and "the default" is not a list anybody sent.
 *
 * Every function here returns a new document and never touches its input:
 * the undo stack holds whole documents, so a mutation would rewrite history.
 */

export type PageRef = { kind: 'home' } | { kind: 'content'; slug: string };

export const HOME: PageRef = { kind: 'home' };

/** The builder's document: a branding document whose arrangements are lists with ids. */
export type Doc = BrandingDocument & { sections: BrandingSection[] };

export function samePage(a: PageRef, b: PageRef): boolean {
  return a.kind === b.kind && (a.kind === 'home' || a.slug === (b as { slug: string }).slug);
}

/** A deep copy, so an operation can mutate its own copy freely. */
function copy<T>(value: T): T {
  return structuredClone(value);
}

/* ------------------------------------------------------------------ */
/* Ids                                                                 */
/* ------------------------------------------------------------------ */

/** The id alphabet (`SECTION_ID_PATTERN`): letters, digits and hyphens. */
function idStem(type: string): string {
  const stem = type.replace(/[^a-z0-9-]/gi, '-').slice(0, 30);
  return stem === '' ? 'section' : stem;
}

/** The first `type`, `type-2`, `type-3`… that nothing on the page uses yet. */
export function freshId(type: string, taken: ReadonlySet<string>): string {
  const stem = idStem(type);
  if (!taken.has(stem)) return stem;
  for (let n = 2; ; n += 1) {
    const candidate = `${stem}-${String(n)}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * **Give every section on one page an id**, the one the site keyed it by.
 *
 * The site's key is `id ?? (first of its type ? type : type-N)`, counting every
 * section of the type, ids or not. An id is written with that same value when
 * it is free, so nothing a guest can see — an anchor, a nav link to
 * `section:services` — moves; only when it is already somebody's id (or not in
 * the id alphabet) does a section get the next free one. Ids that are already
 * there are kept, except a repeat, which the validator would refuse.
 */
export function withIds(sections: readonly BrandingSection[]): BrandingSection[] {
  const taken = new Set<string>();
  for (const section of sections) {
    if (section.id !== undefined && SECTION_ID_PATTERN.test(section.id)) taken.add(section.id);
  }
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  return sections.map((section) => {
    const count = (counts.get(section.type) ?? 0) + 1;
    counts.set(section.type, count);
    const own = section.id;
    if (own !== undefined && SECTION_ID_PATTERN.test(own) && !seen.has(own)) {
      seen.add(own);
      return { ...section, slots: { ...section.slots } };
    }
    const siteKey = count === 1 ? section.type : `${section.type}-${String(count)}`;
    const id =
      SECTION_ID_PATTERN.test(siteKey) && !taken.has(siteKey)
        ? siteKey
        : freshId(section.type, new Set([...taken, ...seen]));
    taken.add(id);
    seen.add(id);
    return { ...section, id, slots: { ...section.slots } };
  });
}

/* ------------------------------------------------------------------ */
/* Reading a stored document                                           */
/* ------------------------------------------------------------------ */

/**
 * A stored row may still say `layout` where the vocabulary now says `variant`
 * (`SlotSpec.alias`). The builder shows and sends the new name; the old key is
 * folded into it here, once. The new key wins when a row carries both.
 */
export type AliasOf = (type: string) => readonly { name: string; alias?: string | undefined }[];

function underCurrentNames(section: BrandingSection, slotsOf: AliasOf): BrandingSection {
  const slots = { ...section.slots };
  for (const spec of slotsOf(section.type)) {
    if (spec.alias === undefined || !(spec.alias in slots)) continue;
    if (slots[spec.name] === undefined) slots[spec.name] = slots[spec.alias];
    delete slots[spec.alias];
  }
  return { ...section, slots };
}

/**
 * The builder's document from a stored one (or a template's, or the one
 * mapped from an owner's answers — all three come in by this door).
 *
 * Nothing is dropped: a block the builder has no controls for, a key it does
 * not edit (`categories`), a slot kind with no editor — all are carried as
 * stored, which is the rule the old editor learned the hard way.
 */
export function fromStored(stored: BrandingDocument, slotsOf: AliasOf): Doc {
  const doc = copy(stored);
  const arranged =
    doc.sections !== undefined && doc.sections.length > 0
      ? doc.sections
      : DEFAULT_ARRANGEMENT.map((type) => ({ type, slots: {} }));
  const pages: NonNullable<BrandingDocument['pages']> = {};
  for (const [slug, page] of Object.entries(doc.pages ?? {})) {
    pages[slug] = {
      ...page,
      sections: withIds(page.sections.map((s) => underCurrentNames(s, slotsOf))),
    };
  }
  return {
    ...doc,
    sections: withIds(arranged.map((s) => underCurrentNames(s, slotsOf))),
    ...(doc.pages === undefined ? {} : { pages }),
  };
}

/* ------------------------------------------------------------------ */
/* One page's arrangement                                              */
/* ------------------------------------------------------------------ */

export function sectionsOf(doc: Doc, page: PageRef): BrandingSection[] {
  if (page.kind === 'home') return doc.sections;
  return doc.pages?.[page.slug]?.sections ?? [];
}

/** The page as a new list; everything else in the document is shared by reference. */
export function withSections(
  doc: Doc,
  page: PageRef,
  change: (sections: BrandingSection[]) => BrandingSection[],
): Doc {
  if (page.kind === 'home') return { ...doc, sections: change(copy(doc.sections)) };
  const existing = doc.pages?.[page.slug];
  if (existing === undefined) return doc;
  return {
    ...doc,
    pages: {
      ...doc.pages,
      [page.slug]: { ...existing, sections: change(copy(existing.sections)) },
    },
  };
}

export function indexOfKey(sections: readonly BrandingSection[], key: string): number {
  return sections.findIndex((section) => section.id === key);
}

/** Move the section at `from` so it lands at `to` (both positions in the list as it was). */
export function moveSection(doc: Doc, page: PageRef, from: number, to: number): Doc {
  return withSections(doc, page, (sections) => {
    if (from < 0 || from >= sections.length || to < 0 || to >= sections.length || from === to) {
      return sections;
    }
    const [moved] = sections.splice(from, 1);
    if (moved !== undefined) sections.splice(to, 0, moved);
    return sections;
  });
}

export function insertSection(
  doc: Doc,
  page: PageRef,
  at: number,
  section: Omit<BrandingSection, 'id'>,
): { doc: Doc; id: string } {
  const taken = new Set(sectionsOf(doc, page).map((s) => s.id ?? ''));
  const id = freshId(section.type, taken);
  const next = withSections(doc, page, (sections) => {
    const where = Math.max(0, Math.min(at, sections.length));
    sections.splice(where, 0, { ...copy(section), id });
    return sections;
  });
  return { doc: next, id };
}

export function duplicateSection(
  doc: Doc,
  page: PageRef,
  index: number,
): { doc: Doc; id: string | null } {
  const source = sectionsOf(doc, page)[index];
  if (source === undefined) return { doc, id: null };
  const { id: _old, ...rest } = source;
  void _old;
  return insertSection(doc, page, index + 1, rest);
}

export function removeSection(doc: Doc, page: PageRef, index: number): Doc {
  return withSections(doc, page, (sections) => {
    if (index >= 0 && index < sections.length) sections.splice(index, 1);
    return sections;
  });
}

/** One slot of one section; `undefined` removes the key, which is what "use the default" is. */
export function setSlot(doc: Doc, page: PageRef, key: string, slot: string, value: unknown): Doc {
  return withSections(doc, page, (sections) => {
    const index = indexOfKey(sections, key);
    const target = sections[index];
    if (target === undefined) return sections;
    const slots = { ...target.slots };
    if (value === undefined) delete slots[slot];
    else slots[slot] = value;
    sections[index] = { ...target, slots };
    return sections;
  });
}

/* ------------------------------------------------------------------ */
/* Content pages                                                       */
/* ------------------------------------------------------------------ */

export function addPage(doc: Doc, slug: string, title: LocalizedText): Doc {
  return { ...doc, pages: { ...(doc.pages ?? {}), [slug]: { title, sections: [] } } };
}

export function removePage(doc: Doc, slug: string): Doc {
  const pages = { ...(doc.pages ?? {}) };
  delete pages[slug];
  const next: Doc = { ...doc, pages };
  if (Object.keys(pages).length === 0) delete next.pages;
  return next;
}

export function setPageTitle(doc: Doc, slug: string, title: LocalizedText): Doc {
  const page = doc.pages?.[slug];
  if (page === undefined) return doc;
  return { ...doc, pages: { ...doc.pages, [slug]: { ...page, title } } };
}

/** The content pages' slugs, in the order a person predicts: alphabetical. */
export function pageSlugs(doc: Doc): string[] {
  return Object.keys(doc.pages ?? {}).sort((a, b) => a.localeCompare(b));
}

/* ------------------------------------------------------------------ */
/* What is published, and what the canvas is shown                     */
/* ------------------------------------------------------------------ */

/** Empty strings and empty lists mean "the site's default"; they are not sent. */
function prune(slots: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(slots)) {
    if (value === '' || value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

/** Empty languages are not a translation; a lone English value collapses back to a string. */
export function pruneLocalized(value: LocalizedText): LocalizedText {
  if (typeof value === 'string') return value;
  const out: Record<string, string> = {};
  for (const [language, text] of Object.entries(value)) if (text !== '') out[language] = text;
  const keys = Object.keys(out);
  const only = keys[0];
  if (keys.length === 1 && only === 'en') return out[only] ?? '';
  return out;
}

function pruneSections(sections: readonly BrandingSection[]): BrandingSection[] {
  return sections.map((section) => ({
    type: section.type,
    ...(section.id === undefined ? {} : { id: section.id }),
    slots: prune(section.slots),
  }));
}

/**
 * **The document as the publish route takes it** — and as the canvas is
 * shown it, so the picture is of what would be published and not of the
 * half-typed state in between.
 *
 * Absent rather than empty for every optional key, the way the server stores
 * them: a tenant with no content pages and no bar publishes the document they
 * had before either existed, byte for byte.
 */
export function forPublish(doc: Doc): BrandingDocument {
  const pages: NonNullable<BrandingDocument['pages']> = {};
  for (const slug of pageSlugs(doc)) {
    const page = doc.pages?.[slug];
    if (page === undefined) continue;
    pages[slug] = { title: pruneLocalized(page.title), sections: pruneSections(page.sections) };
  }
  const nav = (doc.nav ?? []).map((entry) => ({
    label: pruneLocalized(entry.label),
    target: entry.target,
  }));
  const out: BrandingDocument = {
    preset: doc.preset ?? 'serene',
    tokens: { ...(doc.tokens ?? {}) },
    sections: pruneSections(doc.sections),
  };
  if (Object.keys(pages).length > 0) out.pages = pages;
  if (nav.length > 0) out.nav = nav;
  if (doc.categories !== undefined) out.categories = doc.categories;
  if (doc.motion !== undefined && doc.motion !== '' && doc.motion !== 'none') {
    out.motion = doc.motion;
  }
  if (doc.site_title !== undefined && doc.site_title !== '') out.site_title = doc.site_title;
  if (doc.tagline !== undefined && doc.tagline !== '') out.tagline = doc.tagline;
  if (doc.custom_css !== undefined && doc.custom_css !== '') out.custom_css = doc.custom_css;
  return out;
}

/** Whether two documents would publish the same bytes. */
export function samePublished(a: Doc, b: Doc): boolean {
  return JSON.stringify(forPublish(a)) === JSON.stringify(forPublish(b));
}

/* ------------------------------------------------------------------ */
/* The undo stack                                                      */
/* ------------------------------------------------------------------ */

/** How many steps back. Whole documents, a few kilobytes each. */
export const HISTORY_LIMIT = 100;

/** Typing into one field within this long is one step, not one per letter. */
export const COALESCE_MS = 1_200;

export interface History {
  past: Doc[];
  present: Doc;
  future: Doc[];
  /** What the last step was, so the next keystroke in the same field joins it. */
  last: { key: string; at: number } | null;
}

export function startHistory(doc: Doc): History {
  return { past: [], present: doc, future: [], last: null };
}

/**
 * Record a change. A `coalesce` key (a field's name) folds a burst of changes
 * to the same field into the step that began it, so undo takes back a word
 * rather than a letter; anything else — a move, an insert, a preset — is its
 * own step.
 */
export function record(
  history: History,
  next: Doc,
  coalesce: string | null = null,
  now: number = Date.now(),
): History {
  if (next === history.present) return history;
  const joins =
    coalesce !== null &&
    history.last !== null &&
    history.last.key === coalesce &&
    now - history.last.at < COALESCE_MS;
  const past = joins ? history.past : [...history.past, history.present].slice(-HISTORY_LIMIT);
  return {
    past,
    present: next,
    future: [],
    last: coalesce === null ? null : { key: coalesce, at: now },
  };
}

export function undo(history: History): History {
  const previous = history.past[history.past.length - 1];
  if (previous === undefined) return history;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
    last: null,
  };
}

export function redo(history: History): History {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return {
    past: [...history.past, history.present].slice(-HISTORY_LIMIT),
    present: next,
    future: rest,
    last: null,
  };
}

/** Replace the document outright (a template, a restore, a discard) as one undoable step. */
export function replace(history: History, next: Doc): History {
  return record(history, next, null);
}
